/**
 * VoiceMomentRibbon — the first-post "Voice moment" (DEV-1441, variant B;
 * spec DESIGN_FIRST_POST_VOICE_MOMENT.md; Skip chose B 2026-07-08).
 *
 * Shown ONCE per user per lifetime, when a feed-share response carries
 * `firstPost: true` (server-detected; the `first_feed_post` milestone row
 * is the durable record). A dismissible ribbon above the feed — NOT a
 * modal (hero modals are reserved for the ring animation and medals) —
 * paired with a one-shot glow on the new post card (host applies
 * `styles.cardGlow` via the exported class).
 *
 * Celebration only: confers no status, gates nothing (the Contributor
 * trigger stays the rung basics — decided 2026-07-08). Copy is
 * admin-overridable through the onboarding_copy layer (host passes the
 * resolved strings when it has them; falls back to static i18n).
 *
 * Accent: interim `--info-text` — the final Voice color rides the
 * DEV-1301 dimension palette (TODO when that locks).
 */

import React from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import i18n from '../../i18n/config';
import { useOnboardingCopy, resolveCopyOverride } from '../../api/onboardingCopy';
import styles from './VoiceMomentRibbon.module.css';

export interface VoiceMomentRibbonProps {
  open: boolean;
  onDismiss: () => void;
}

/** Class the host puts on the freshly shared card for the one-shot glow. */
export const VOICE_GLOW_CLASS = styles.cardGlow;

const VoiceMomentRibbon: React.FC<VoiceMomentRibbonProps> = ({ open, onDismiss }) => {
  const { t } = useAppTranslation('pages');
  // Admin copy overrides (onboarding_copy) — fetched only while visible;
  // three-tier resolution: DB override → static locale JSON → English.
  const { copyMap } = useOnboardingCopy(open);
  if (!open) return null;

  const lang = i18n.language || 'en';
  const title =
    resolveCopyOverride(copyMap, lang, 'pages:feed.voiceMoment.title') ??
    t('feed.voiceMoment.title');
  const body =
    resolveCopyOverride(copyMap, lang, 'pages:feed.voiceMoment.body') ??
    t('feed.voiceMoment.body');

  return (
    <div className={styles.ribbon} role="status">
      <span className={styles.glyph} aria-hidden>
        📣
      </span>
      <div className={styles.text}>
        <div className={styles.title}>{title}</div>
        <div className={styles.body}>{body}</div>
      </div>
      <button
        type="button"
        className={styles.close}
        onClick={onDismiss}
        aria-label={t('feed.voiceMoment.dismiss')}
      >
        ×
      </button>
    </div>
  );
};

export default VoiceMomentRibbon;
